import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@core/lib/utils";

export interface QuotaChipProps {
  used: number;
  /** Limit; use 0 for "not available", 999999 for "unlimited". */
  limit: number;
  /**
   * Approche de la limite (≥ 80 %, cf. `useFreemiumQuota`). L'ancien bandeau
   * portait cet état ; la pastille de la V2 l'avait perdu, si bien qu'elle
   * restait grise jusqu'au mur — 14/15 conversations s'affichait exactement
   * comme 0/15. Régression du redesign, corrigée le 18/09.
   */
  nearLimit?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * Pill-style quota indicator for the dashboard header. Replaces the large
 * banner when `enableRedesignPollsV2` is on. Clicking opens the journal
 * (or any handler the parent provides).
 */
export function QuotaChip({
  used,
  limit,
  nearLimit = false,
  onClick,
  className,
}: QuotaChipProps) {
  const { t } = useTranslation("polls");
  const isUnlimited = limit >= 999999;
  const isUnavailable = limit === 0;
  const isOverLimit = !isUnlimited && !isUnavailable && used >= limit;
  const isWarning = nearLimit && !isOverLimit && !isUnlimited && !isUnavailable;

  // En approche de la limite, la pastille DIT ce qui reste au lieu de compter
  // ce qui est consommé. Un changement de couleur seul ne se voit pas de tout
  // le monde, et « 14/15 » demande un calcul que « plus que 1 » évite.
  const label = isUnavailable
    ? t("quota_chip_unavailable")
    : isUnlimited
      ? t("quota_chip_unlimited")
      : isWarning
        ? t("quota_chip_remaining", { count: Math.max(limit - used, 0) })
        : t("quota_chip_count", { used, limit });

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
        "border",
        isOverLimit
          ? "bg-destructive/10 text-destructive border-destructive/30 hover:bg-destructive/15"
          : isWarning
            ? "bg-warning-bg text-warning border-warning/40 hover:bg-warning-bg/80"
            : "bg-muted text-muted-foreground border-border hover:bg-muted/80",
        className,
      )}
      aria-label={t("quota_chip_aria", { label })}
      data-testid="quota-chip"
    >
      <FileText className="h-3 w-3" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
